/**
 * Build per-node inspection slices from cached Studio run events + final payload.
 */

export function extractRunPayload(lastSnapshot) {
  if (!lastSnapshot || typeof lastSnapshot !== 'object') return null;
  const data = lastSnapshot.data ?? lastSnapshot;
  if (!data || typeof data !== 'object') return null;

  if (data.variables || data.node_outputs || data.result || data.execution_path) {
    return data;
  }

  const events = Array.isArray(data.events) ? data.events : [];
  const flowComplete = [...events].reverse().find((e) => e?.type === 'flow_complete');
  if (flowComplete) {
    const inner = flowComplete.data ?? flowComplete.result ?? flowComplete;
    if (inner?.variables || inner?.node_outputs) return inner;
    if (inner?.result && typeof inner.result === 'object') {
      return {
        ...inner,
        ...(inner.result && typeof inner.result === 'object' ? inner.result : {}),
      };
    }
    return inner;
  }

  if (data.lastEvent?.type === 'flow_complete') {
    const inner = data.lastEvent.data ?? data.lastEvent.result ?? data.lastEvent;
    return inner?.result ?? inner;
  }

  return data;
}

export function resolveNodeIdFromEvent(event) {
  if (!event || typeof event !== 'object') return '';
  const meta = event.langgraph_v2 || {};
  return String(
    event.node_id
    || event.nodeId
    || meta.name
    || '',
  ).trim();
}

export function collectEventsForNode(events, nodeId) {
  const nid = String(nodeId || '').trim();
  if (!nid) return [];
  return (Array.isArray(events) ? events : []).filter((ev) => resolveNodeIdFromEvent(ev) === nid);
}

export function buildNodeInspection({ events = [], runPayload = null, nodeId }) {
  const nid = String(nodeId || '').trim();
  const nodeEvents = collectEventsForNode(events, nid);
  const payload = runPayload && typeof runPayload === 'object' ? runPayload : {};

  const variablesRoot = payload.variables && typeof payload.variables === 'object'
    ? payload.variables
    : {};
  const nodeOutputsRoot = payload.node_outputs && typeof payload.node_outputs === 'object'
    ? payload.node_outputs
    : (variablesRoot.node_outputs && typeof variablesRoot.node_outputs === 'object'
      ? variablesRoot.node_outputs
      : {});

  const startEvent = nodeEvents.find((e) => e.type === 'node_start' || e.langgraph_v2?.event === 'on_chain_start');
  const completeEvent = nodeEvents.find((e) => e.type === 'node_complete' || e.langgraph_v2?.event === 'on_chain_end');
  const errorEvent = nodeEvents.find((e) => e.type === 'node_error');

  const pathEntry = (Array.isArray(payload.execution_path) ? payload.execution_path : [])
    .find((entry) => {
      const id = typeof entry === 'object' ? entry.node_id : entry;
      return String(id) === nid;
    });

  return {
    nodeId: nid,
    nodeEvents,
    startPayload: startEvent?.data ?? null,
    completePayload: completeEvent?.data ?? null,
    error: errorEvent?.error || errorEvent?.message || null,
    duration: completeEvent?.node_duration ?? null,
    nodeOutput: nodeOutputsRoot[nid] ?? nodeOutputsRoot[nid.replace(/^node_/, '')] ?? null,
    variables: variablesRoot,
    pathEntry: pathEntry ?? null,
    llmTokens: nodeEvents.filter((e) => e.type === 'llm_token').map((e) => e.token).filter(Boolean),
    toolEvents: nodeEvents.filter((e) => e.type?.startsWith?.('tool_')),
  };
}
